import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatCurrency as money } from '../lib/format';
import { Tag, RefreshCw, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '../lib/api';
import { toast } from '../lib/toast';

interface DealProductRow {
  productId: string;
  productName: string;
  finalPrice: number;
  amCut: number;
  supervisorCut?: number;
  houseKeep: number;
  currentBasePrice: number | null;
}

interface AdminDealRow {
  _id: string;
  accountId: string;
  accountName: string;
  name: string;
  products: DealProductRow[];
  startDate: string;
  endDate?: string;
  status: 'active' | 'ended' | 'cancelled';
  note?: string;
}

const statusStyles: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  ended: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-700'
};

function fmtDate(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Every negotiated deal across all clinics.
 *
 * The per-clinic panel answers "what has this clinic got?"; this answers
 * "what are we committed to right now?" — including the deals that have gone
 * underwater since a product's pharmacy cost rose.
 */
export default function ClinicDeals() {
  const [rows, setRows] = useState<AdminDealRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiClient.getClinicDeals({ status: statusFilter || undefined });
    setLoading(false);
    if (res.success) setRows((res as any).deals || res.data?.deals || []);
    else toast.error(res.error || 'Failed to load deals');
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleEnd = async (id: string) => {
    setBusyId(id);
    const res = await apiClient.endClinicDeal(id);
    setBusyId(null);
    if (res.success) { toast.success('Deal ended'); load(); }
    else toast.error(res.error || 'Failed to end deal');
  };

  // Deals now losing money because pharmacy cost rose after they were struck.
  const underwater = useMemo(
    () => rows.filter(d => d.status === 'active' && d.products.some(p => p.houseKeep < 0)),
    [rows]
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-gray-600" />
          <h1 className="text-xl font-semibold text-gray-900">Negotiated deals</h1>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
          >
            <option value="active">Active</option>
            <option value="ended">Ended</option>
            <option value="cancelled">Cancelled</option>
            <option value="">All</option>
          </select>
          <button
            onClick={load}
            className="flex items-center gap-1.5 border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        A deal overrides catalog pricing for its clinic — even when the catalog is cheaper — and pays
        the AM and supervisor the exact per-unit cuts shown. Deals are created from the clinic's own
        record; this view is read-only apart from ending one.
      </p>

      {underwater.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>
            <strong>{underwater.length} active deal{underwater.length === 1 ? '' : 's'} now pay out more
            than they collect above pharmacy cost.</strong> Base prices rose after these were struck.
            Deal prices are commitments and are never adjusted automatically — end or renegotiate them.
          </span>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400">No deals match this filter.</p>
      ) : (
        <div className="space-y-3">
          {rows.map(deal => {
            const isUnderwater = deal.status === 'active' && deal.products.some(p => p.houseKeep < 0);
            return (
              <div
                key={deal._id}
                className={`bg-white border rounded-lg p-4 ${isUnderwater ? 'border-red-300' : 'border-gray-200'}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/users?userId=${deal.accountId}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-700"
                      >
                        {deal.accountName}
                      </Link>
                      <span className="text-sm text-gray-500">·</span>
                      <span className="text-sm text-gray-900">{deal.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusStyles[deal.status]}`}>
                        {deal.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {fmtDate(deal.startDate)} → {deal.endDate ? fmtDate(deal.endDate) : 'open-ended'}
                    </div>
                  </div>
                  {deal.status === 'active' && (
                    <button
                      onClick={() => handleEnd(deal._id)}
                      disabled={busyId === deal._id}
                      className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50 whitespace-nowrap"
                    >
                      {busyId === deal._id ? 'Ending…' : 'End deal'}
                    </button>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 text-left border-b border-gray-100">
                        <th className="py-1 pr-3 font-medium">Product</th>
                        <th className="py-1 px-2 font-medium text-right">Clinic pays</th>
                        <th className="py-1 px-2 font-medium text-right">Pharmacy</th>
                        <th className="py-1 px-2 font-medium text-right">House</th>
                        <th className="py-1 px-2 font-medium text-right">Supervisor</th>
                        <th className="py-1 pl-2 font-medium text-right">AM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deal.products.map(p => (
                        <tr key={p.productId} className="border-b border-gray-50 last:border-0">
                          <td className="py-1.5 pr-3 text-gray-900">{p.productName}</td>
                          <td className="py-1.5 px-2 text-right font-medium">{money(p.finalPrice)}</td>
                          <td className="py-1.5 px-2 text-right text-gray-600">
                            {p.currentBasePrice === null ? '—' : money(p.currentBasePrice)}
                          </td>
                          <td className={`py-1.5 px-2 text-right font-medium ${p.houseKeep < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                            {money(p.houseKeep)}
                          </td>
                          <td className="py-1.5 px-2 text-right text-gray-600">{money(p.supervisorCut || 0)}</td>
                          <td className="py-1.5 pl-2 text-right text-gray-600">{money(p.amCut)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {deal.note && <p className="text-xs text-gray-500 mt-2 italic">{deal.note}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
